import { formatVolumeParts, formatDuration } from '../workoutSummary';

/**
 * Sceglie il protagonista della card condivisibile. Funzione PURA e
 * indipendente dal renderer: dati dentro, descrizione della card fuori.
 *
 * Cascata (primo match vince):
 *  1. volume, se rappresentativo (>=3 serie pesate E >=200 kg);
 *  2. badge epico/leggendario appena sbloccato;
 *  3. level-up;
 *  4. streak, solo su milestone (7/14/21/30/50/100);
 *  5. durata >= 15 minuti;
 *  6. serie completate;
 *  7. esercizi (fallback assoluto).
 *
 * Garanzie:
 *  - mai "0 kg": un volume non rappresentativo è ESCLUSO dalla card
 *    (né eroe né statistica secondaria);
 *  - il "momento" secondario non duplica mai l'eroe;
 *  - output: 1 eroe, max 3 statistiche, max 1 momento, angoli streak/XP.
 *
 * NB: il PR (variant 'gold') è fuori perimetro in questo rilascio; la
 * cascata lo accoglierà in testa quando sarà verificabile lato server.
 */

export const STREAK_MILESTONES = [7, 14, 21, 30, 50, 100];
const VOLUME_MIN_WEIGHTED_SETS = 3;
const VOLUME_MIN_KG = 200;
const DURATION_MIN_MINUTES = 15;
const STREAK_MIN_FOR_DISPLAY = 3; // sotto, la streak non compare da nessuna parte

const RARE_RARITIES = new Set(['epico', 'leggendario']);

export const chooseHeroMetric = ({
  summary,
  prData = null, // riservato alla V2 (variant 'gold'); ignorato se non verified
  newBadges = [],
  xpResult = null,
  streak = 0,
} = {}) => {
  const s = summary || { durationMinutes: 0, exercisesCount: 0, setsCount: 0, totalVolumeKg: 0, weightedSetsCount: 0 };

  const volumeParts = formatVolumeParts(s.totalVolumeKg);
  const volumeRepresentative =
    (s.weightedSetsCount || 0) >= VOLUME_MIN_WEIGHTED_SETS &&
    (s.totalVolumeKg || 0) >= VOLUME_MIN_KG &&
    !!volumeParts;

  const rareBadge = (newBadges || []).find((b) => RARE_RARITIES.has(b.rarity)) || null;
  const anyBadge = (newBadges || [])[0] || null;
  const leveledUp = !!(xpResult && xpResult.leveledUp);
  const streakMilestone = STREAK_MILESTONES.includes(streak);
  const streakVisible = streak >= STREAK_MIN_FOR_DISPLAY;

  // ── 1. Eroe ──
  let hero;
  if (prData && prData.verified === true) {
    // V2: variant 'gold'. Mai attivo in questo rilascio (prData non fornito).
    hero = { type: 'pr', value: prData.value, unit: prData.unit || 'kg', label: `NUOVO RECORD · ${prData.exerciseName || ''}`.trim() };
  } else if (volumeRepresentative) {
    hero = { type: 'volume', value: volumeParts.value, unit: volumeParts.unit, label: 'VOLUME SOLLEVATO' };
  } else if (rareBadge) {
    hero = { type: 'badge', value: rareBadge.name, unit: '', label: 'BADGE SBLOCCATO', sublabel: rareBadge.description, icon: 'medal' };
  } else if (leveledUp) {
    hero = { type: 'levelup', value: String(xpResult.currentLevel), unit: '', label: 'NUOVO LIVELLO RAGGIUNTO', icon: 'level' };
  } else if (streakMilestone) {
    hero = { type: 'streak', value: String(streak), unit: 'giorni', label: 'DI ALLENAMENTO DI FILA', icon: 'flame' };
  } else if ((s.durationMinutes || 0) >= DURATION_MIN_MINUTES) {
    const d = s.durationMinutes;
    hero = d < 60
      ? { type: 'duration', value: String(d), unit: 'min', label: 'DI ALLENAMENTO' }
      : { type: 'duration', value: formatDuration(d), unit: '', label: 'DI ALLENAMENTO' };
  } else if ((s.setsCount || 0) >= 1) {
    hero = { type: 'sets', value: String(s.setsCount), unit: s.setsCount === 1 ? 'serie' : 'serie', label: 'COMPLETATE OGGI' };
  } else {
    hero = { type: 'exercises', value: String(s.exercisesCount || 0), unit: s.exercisesCount === 1 ? 'esercizio' : 'esercizi', label: 'COMPLETATI OGGI' };
  }

  // ── 2. Statistiche secondarie (max 3), mai duplicano l'eroe,
  //       mai il volume se non rappresentativo ──
  const candidates = [];
  if (hero.type !== 'duration' && (s.durationMinutes || 0) > 0) {
    candidates.push({ key: 'duration', value: String(s.durationMinutes), unit: 'min', label: 'DURATA' });
  }
  if (hero.type !== 'volume' && volumeRepresentative) {
    candidates.push({ key: 'volume', value: volumeParts.value, unit: volumeParts.unit, label: 'VOLUME' });
  }
  if (hero.type !== 'exercises' && (s.exercisesCount || 0) > 0) {
    candidates.push({ key: 'exercises', value: String(s.exercisesCount), unit: '', label: s.exercisesCount === 1 ? 'ESERCIZIO' : 'ESERCIZI' });
  }
  if (hero.type !== 'sets' && (s.setsCount || 0) > 0) {
    candidates.push({ key: 'sets', value: String(s.setsCount), unit: '', label: 'SERIE' });
  }
  if (hero.type !== 'streak' && streakVisible && candidates.length < 3) {
    candidates.push({ key: 'streak', value: String(streak), unit: '', label: 'STREAK', icon: 'flame' });
  }
  const stats = candidates.slice(0, 3);

  // ── 3. Momento (chip compatto), mai duplicato dell'eroe ──
  let moment = null;
  if (hero.type !== 'badge' && anyBadge) {
    moment = { type: 'badge', icon: 'medal', title: anyBadge.name, subtitle: anyBadge.description };
  } else if (hero.type !== 'levelup' && leveledUp) {
    moment = { type: 'levelup', icon: 'level', title: `Livello ${xpResult.currentLevel}`, subtitle: 'Nuovo livello raggiunto' };
  }

  // ── 4. Angoli: streak (se visibile e non già usata) e XP ──
  const streakUsed = hero.type === 'streak' || stats.some((x) => x.key === 'streak');
  const corner = {
    streak: streakVisible && !streakUsed ? streak : null,
    xp: xpResult && Number(xpResult.xpGained) > 0
      ? { gained: xpResult.xpGained, level: xpResult.currentLevel }
      : null,
  };

  return { variant: prData && prData.verified === true ? 'gold' : 'classic', hero, stats, moment, corner };
};
